import { supabase } from '../../../config/supabase';
import { getInitialAssessmentSourceIssues, normalizeInitialAssessmentPayload } from '../initial-assessment/initialAssessmentDomain';
import { isMedicalRecordInvalidated } from '../../../utils/medicalRecordStatus';
import { AssessmentResultsV2Service } from './AssessmentResultsV2Service';

export class InitialAssessmentIntegrityService {
  static async assertSourcesCurrent(value: unknown, identity: { clientId: string; patientId: string }): Promise<void> {
    const payload = normalizeInitialAssessmentPayload(value);
    if (!payload) throw new Error('A avaliação clínica não possui conteúdo disponível.');
    const selected = payload.selectedResults;
    if (selected.length) {
      const results = await AssessmentResultsV2Service.listByIds(identity.clientId, selected.map((source) => source.resultId));
      if (getInitialAssessmentSourceIssues(selected, results, identity).length) {
        throw new Error('Um teste vinculado à avaliação foi alterado, invalidado ou removido. Reabra a ficha e revise os testes selecionados.');
      }
    }
    if (payload.anamnesisSnapshot) {
      const { data, error } = await supabase.from('medical_records')
        .select('recordId, recordType, recordAttachments')
        .eq('clientId', identity.clientId).eq('patientId', identity.patientId)
        .eq('recordId', payload.anamnesisSnapshot.sourceRecordId).maybeSingle();
      if (error) throw error;
      if (!data || isMedicalRecordInvalidated(data.recordType, data.recordAttachments)) {
        throw new Error('A anamnese vinculada não está ativa no prontuário deste paciente. Revise a ficha antes de emitir.');
      }
    }
    if (payload.baselineResultId) {
      const [baseline] = await AssessmentResultsV2Service.listByIds(identity.clientId, [payload.baselineResultId]);
      if (!baseline || baseline.patientId !== identity.patientId || baseline.status !== 'completed' || baseline.testKey !== 'INITIAL_ASSESSMENT') {
        throw new Error('A avaliação basal não está disponível para este paciente. Revise o vínculo na ficha.');
      }
    }
    if (payload.externalExams.length) {
      const { data, error } = await supabase.from('medical_records')
        .select('recordId, recordTitle, recordDate, recordType, recordAttachments, recordUpdatedAt')
        .eq('clientId', identity.clientId).eq('patientId', identity.patientId)
        .eq('recordType', 'exam').in('recordId', payload.externalExams.map((exam) => exam.recordId));
      if (error) throw error;
      const records = new Map((data ?? []).map((record) => [record.recordId, record]));
      for (const source of payload.externalExams) {
        const record = records.get(source.recordId);
        const revision = source.sourceUpdatedAt ?? null;
        const currentRevision = record?.recordUpdatedAt ?? null;
        const revisionMatches = revision === currentRevision || (revision != null && currentRevision != null && Date.parse(revision) === Date.parse(currentRevision));
        if (!record || isMedicalRecordInvalidated(record.recordType, record.recordAttachments)
          || Date.parse(record.recordDate) !== Date.parse(source.recordDate)
          || ((record.recordTitle || '').trim() || 'Exame complementar') !== source.title || !revisionMatches) {
          throw new Error('Um exame complementar vinculado foi alterado, invalidado ou removido. Revise as referências na ficha clínica.');
        }
      }
    }
  }
}
